/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { pd } from '@airasia-phoenix/react-native/tokens';
import {
  getColor,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import ButtonView from '@airasia-phoenix/react-native/components/ButtonView';
import { ButtonViewType } from '@airasia-phoenix/react-native/components/ButtonView/types';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import Close from '@move-travel/react-native-phoenix-icons/Close';
import { useDeviceInfo } from '@airasia-rn/ui';
import { useFlagEnabled } from '@airasia-rn/account/hooks/settings';
import { getCookie, setCookie } from '../../utils/cookies';
import { styles } from './styles';
import { LoginTooltipProps } from './types';
import {
  LOGIN_TOOLTIP_AFTER_ONE_TAP_ONLY,
  LOGIN_TOOLTIP_COOKIE_NAME,
  LOGIN_TOOLTIP_COOKIE_VALUE,
} from './constants';

const DEFAULT_COOLDOWN_MINUTES = 7 * 24 * 60;

const LoginTooltip = ({
  data,
  isLoggedIn,
  isGoogleOneTapSkippedOrDismissed = false,
  bottomTabHeight = 0,
  onView,
  onClose,
  onLogin,
}: LoginTooltipProps) => {
  const { isMobileWidth } = useDeviceInfo();
  const { enabled: waitForOneTap } = useFlagEnabled(
    LOGIN_TOOLTIP_AFTER_ONE_TAP_ONLY,
    true,
  );
  const [visible, setVisible] = useState(false);
  const hasTrackedViewEventRef = useRef(false);

  useEffect(() => {
    if (!data || isLoggedIn) {
      setVisible(false);
      return;
    }

    if (waitForOneTap && !isGoogleOneTapSkippedOrDismissed) {
      setVisible(false);
      return;
    }

    if (getCookie(LOGIN_TOOLTIP_COOKIE_NAME) !== LOGIN_TOOLTIP_COOKIE_VALUE) {
      setVisible(true);
    }
  }, [data, isLoggedIn, isGoogleOneTapSkippedOrDismissed, waitForOneTap]);

  useEffect(() => {
    if (visible && !hasTrackedViewEventRef.current) {
      onView?.();
      hasTrackedViewEventRef.current = true;
      return;
    }

    if (!visible) {
      hasTrackedViewEventRef.current = false;
    }
  }, [onView, visible]);

  const onLoginPress = () => {
    setVisible(false);
    onLogin();
  };

  const handleClose = () => {
    onClose?.();
    const minutes = Number(data?.coolDownDuration);
    const cooldownMinutes =
      Number.isFinite(minutes) && minutes > 0
        ? minutes
        : DEFAULT_COOLDOWN_MINUTES;
    setCookie(LOGIN_TOOLTIP_COOKIE_NAME, LOGIN_TOOLTIP_COOKIE_VALUE, {
      minutes: cooldownMinutes,
    });
    setVisible(false);
  };

  if (!visible || !data) return null;

  const closeIcon = (
    <TouchableOpacity
      onPress={handleClose}
      style={isMobileWidth ? styles.closeButtonMobile : styles.closeButton}
    >
      <IconView
        tint={getColor(pd.sys.colour.content_secondary)}
        width={24}
        height={24}
      >
        <Close />
      </IconView>
    </TouchableOpacity>
  );

  const loginButton = (
    <ButtonView
      type={ButtonViewType.SECONDARY_SMALL}
      text={data.loginButton.text}
      onPress={onLoginPress}
      disabled={false}
    />
  );

  if (isMobileWidth) {
    return (
      <View style={[styles.mobileWrapper, { bottom: bottomTabHeight }]}>
        <View style={styles.containerMobile}>
          {closeIcon}
          <View style={styles.infoTextWrapperMobile}>
            <TextView
              text={data.text}
              style={[
                styles.infoTextMobile,
                getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_primary,
                ),
              ]}
            />
          </View>
          <View style={styles.loginButtonWrapperMobile}>{loginButton}</View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.desktopWrapper}>
      <View style={styles.container}>
        <View style={styles.contentWrapper}>
          <View style={styles.infoTextWrapper}>
            <TextView
              spannableParentTextStyle={styles.infoTextParent}
              text={data.text}
              style={[
                getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_primary,
                ),
                styles.infoText,
              ]}
            />
          </View>
          {loginButton}
        </View>
        {closeIcon}
      </View>
    </View>
  );
};

export default LoginTooltip;
